import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import axios from "axios";
import {
  WORKFLOW_DISPLAY_DEFAULTS,
  buildWorkflowDisplayDraft,
} from "../../../../constants/workflowDisplayDefaults";

const defaultDraft = {
  id: null,
  label: "",
  parentStatusKey: "Quote Requested",
  sortOrder: "",
  isActive: true,
};

const toOrderValue = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export default function SubStatusesTab({ instanceUniqueId }) {
  const [subStatuses, setSubStatuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(defaultDraft);
  const [instanceId, setInstanceId] = useState(null);
  const [workflowDraft, setWorkflowDraft] = useState(buildWorkflowDisplayDraft({}));

  const SERVER = process.env.REACT_APP_NODEJS_SERVER_URL;
  const headers = { headers: { accessToken: localStorage.getItem("accessToken") } };

  const foundationalStatuses = useMemo(() => {
    return (WORKFLOW_DISPLAY_DEFAULTS.SO || []).map((row) => {
      const label = workflowDraft?.SO?.[row.statusKey]?.displayLabel || row.displayLabel;
      return {
        key: row.statusKey,
        label,
      };
    });
  }, [workflowDraft]);

  const statusLabelByKey = useMemo(() => {
    const map = {};
    foundationalStatuses.forEach((row) => {
      map[row.key] = row.label;
    });
    return map;
  }, [foundationalStatuses]);

  const fetchInstanceId = async () => {
    if (!instanceUniqueId) return;
    const res = await axios.get(
      `${SERVER}/instances/byinstanceUniqueId/${instanceUniqueId}`,
      headers
    );
    setInstanceId(res.data?.id || null);
  };

  const fetchDefaults = async (targetInstanceId = instanceId) => {
    if (!targetInstanceId) return;
    const res = await axios.get(`${SERVER}/instances/${targetInstanceId}/defaults`, headers);
    const defaults = res.data?.defaults || {};
    setWorkflowDraft(buildWorkflowDisplayDraft(defaults.workflowDisplaySettings || {}));
  };

  const fetchSubStatuses = async (targetInstanceId = instanceId) => {
    if (!targetInstanceId) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${SERVER}/instances/${targetInstanceId}/project-sub-statuses`,
        headers
      );
      setSubStatuses(res.data.subStatuses || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInstanceId();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceUniqueId]);

  useEffect(() => {
    if (!instanceId) return;
    fetchDefaults(instanceId);
    fetchSubStatuses(instanceId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceId]);

  const openCreate = () => {
    setDraft({
      ...defaultDraft,
      parentStatusKey: foundationalStatuses?.[0]?.key || defaultDraft.parentStatusKey,
    });
    setOpen(true);
  };

  const openEdit = (subStatus) => {
    setDraft({
      id: subStatus.id,
      label: subStatus.label || "",
      parentStatusKey: subStatus.parentStatusKey || foundationalStatuses?.[0]?.key || defaultDraft.parentStatusKey,
      sortOrder: subStatus.sortOrder ?? "",
      isActive: subStatus.isActive !== false,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!instanceId) return;

    const payload = {
      label: draft.label.trim(),
      parentStatusKey: draft.parentStatusKey,
      sortOrder: toOrderValue(draft.sortOrder),
      isActive: draft.isActive !== false,
    };

    if (!payload.label || !payload.parentStatusKey) return;

    if (draft.id) {
      await axios.put(
        `${SERVER}/instances/${instanceId}/project-sub-statuses/${draft.id}`,
        payload,
        headers
      );
    } else {
      await axios.post(
        `${SERVER}/instances/${instanceId}/project-sub-statuses`,
        payload,
        headers
      );
    }

    setOpen(false);
    fetchSubStatuses(instanceId);
  };

  const remove = async (subStatus) => {
    if (!instanceId) return;
    await axios.delete(
      `${SERVER}/instances/${instanceId}/project-sub-statuses/${subStatus.id}`,
      headers
    );
    fetchSubStatuses(instanceId);
  };

  const isValid = useMemo(() => {
    if (!draft.label.trim()) return false;
    if (!draft.parentStatusKey) return false;
    return draft.sortOrder === "" || Number.isFinite(Number(draft.sortOrder));
  }, [draft]);

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Sub Statuses
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            Configure sub statuses by foundational status for this instance.
          </Typography>
        </Box>

        <Button variant="contained" onClick={openCreate}>
          Add Sub Status
        </Button>
      </Box>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Sub Status Label</TableCell>
            <TableCell>Foundational Status</TableCell>
            <TableCell>Sort Order</TableCell>
            <TableCell>Active</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(subStatuses || []).map((subStatus) => (
            <TableRow key={subStatus.id} hover>
              <TableCell sx={{ fontWeight: 700 }}>{subStatus.label}</TableCell>
              <TableCell>{statusLabelByKey[subStatus.parentStatusKey] || subStatus.parentStatusKey}</TableCell>
              <TableCell>{subStatus.sortOrder ?? "—"}</TableCell>
              <TableCell>{subStatus.isActive !== false ? "Yes" : "No"}</TableCell>
              <TableCell align="right">
                <IconButton onClick={() => openEdit(subStatus)} size="small">
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
                <IconButton onClick={() => remove(subStatus)} size="small">
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
          {!loading && (subStatuses || []).length === 0 && (
            <TableRow>
              <TableCell colSpan={5} sx={{ py: 4, textAlign: "center", opacity: 0.7 }}>
                No sub statuses yet. Add your first one.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>
          {draft.id ? "Edit Sub Status" : "Create Sub Status"}
        </DialogTitle>
        <DialogContent sx={{ display: "grid", gap: 2, pt: 1 }}>
          <TextField
            label="Sub Status Label"
            value={draft.label}
            onChange={(e) => setDraft((prev) => ({ ...prev, label: e.target.value }))}
          />
          <TextField
            select
            label="Foundational Status"
            value={draft.parentStatusKey}
            onChange={(e) => setDraft((prev) => ({ ...prev, parentStatusKey: e.target.value }))}
          >
            {foundationalStatuses.map((status) => (
              <MenuItem key={status.key} value={status.key}>
                {status.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Sort Order"
            type="number"
            value={draft.sortOrder}
            onChange={(e) => setDraft((prev) => ({ ...prev, sortOrder: e.target.value }))}
            helperText="Optional. Lower numbers appear first."
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={draft.isActive}
                onChange={(e) => setDraft((prev) => ({ ...prev, isActive: e.target.checked }))}
              />
            }
            label="Active"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={!isValid}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
